import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { CircleAlert } from 'lucide-react';
import StyledModal from '../../../../components/modal/styledModals/StyledModal';
import SvgFragment from '../../../../fragments/SvgFragment';
import { styles } from '../../../../styles/themes/style';

interface Props {
  show: boolean;
  /** Leads the import source reported as already connected with the sender. */
  connectedCount: number;
  /** The campaign's current excludeFirstDegree — what the modal opens on. */
  excludeFirstDegree: boolean;
  /** Messages only reach connections, so without an invite step skipping them leaves no one. */
  sequenceMessagesWithoutInvite: boolean;
  saving?: boolean;
  onHide: () => void;
  onConfirm: (excludeFirstDegree: boolean) => void;
}

/*
  Asked between Choose recipients and Outreach sequence, only when some leads are
  known to be 1st degree — the same setting as the toggle on Campaign settings.
*/
const CampaignConnectedLeadsModal: React.FC<Props> = ({
  show, connectedCount, excludeFirstDegree, sequenceMessagesWithoutInvite, saving = false, onHide, onConfirm,
}) => {
  const [exclude, setExclude] = useState(excludeFirstDegree);
  useEffect(() => { if (show) setExclude(excludeFirstDegree); }, [show, excludeFirstDegree]);

  const leadsLabel = connectedCount === 1 ? '1 lead is' : `${connectedCount} leads are`;

  return (
    <StyledModal show={show} onHide={onHide} width="520px" backdrop="static">
      <Body>
        <Header>
          <Title>{leadsLabel} already connected with you</Title>
          <CloseBtn type="button" onClick={onHide} aria-label="Close">
            <SvgFragment type="close" />
          </CloseBtn>
        </Header>
        <Lead>What should the campaign do with people you’re already connected with on LinkedIn?</Lead>

        <Options role="radiogroup">
          <Option type="button" role="radio" aria-checked={!exclude} $active={!exclude} onClick={() => setExclude(false)}>
            <RadioDot $active={!exclude} aria-hidden />
            <OptionText>
              <OptionTitle>Keep them, skip the invite</OptionTitle>
              <OptionSub>They stay in the campaign. The invite is skipped and the rest of the sequence runs.</OptionSub>
            </OptionText>
          </Option>
          <Option type="button" role="radio" aria-checked={exclude} $active={exclude} onClick={() => setExclude(true)}>
            <RadioDot $active={exclude} aria-hidden />
            <OptionText>
              <OptionTitle>Skip them, don’t contact them</OptionTitle>
              <OptionSub>They stay in the campaign, but won’t be contacted.</OptionSub>
            </OptionText>
          </Option>
        </Options>

        {exclude && sequenceMessagesWithoutInvite && (
          <Warn>
            <CircleAlert size={13} strokeWidth={2} />
            This sequence only messages people — skipping your connections leaves no one it can message.
          </Warn>
        )}

        <Footer>
          <GhostBtn type="button" onClick={onHide} disabled={saving}>Go back</GhostBtn>
          <PrimaryBtn type="button" onClick={() => onConfirm(exclude)} disabled={saving}>
            {saving ? 'Saving…' : 'Continue'}
          </PrimaryBtn>
        </Footer>
      </Body>
    </StyledModal>
  );
};

export default CampaignConnectedLeadsModal;

const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 24px 26px;
`;

const Header = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
`;

const Title = styled.h3`
  margin: 0;
  font-family: NunitoSans;
  font-size: 18px;
  font-weight: 800;
  line-height: 23px;
  color: #333951;
`;

const CloseBtn = styled.button`
  flex: 0 0 auto;
  width: 24px;
  height: 24px;
  border: none;
  background: none;
  cursor: pointer;
  padding: 2px;
  line-height: 0;
  color: #8C96AD;
  .svg { width: 20px; height: 20px; }
  &:hover { color: #64748b; }
`;

const Lead = styled.p`
  margin: -8px 0 0;
  font-family: NunitoSans;
  font-size: 14px;
  line-height: 21px;
  color: #475569;
`;

const Options = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Option = styled.button<{ $active: boolean }>`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  width: 100%;
  padding: 12px 14px;
  text-align: left;
  cursor: pointer;
  border-radius: 10px;
  border: 1px solid ${({ $active }) => $active ? styles.colors.primary600 : '#e2e8f0'};
  background: ${({ $active }) => $active ? '#f5f7ff' : '#fff'};
  &:hover { border-color: ${styles.colors.primary600}; }
`;

const RadioDot = styled.span<{ $active: boolean }>`
  flex: 0 0 auto;
  width: 16px;
  height: 16px;
  margin-top: 2px;
  border-radius: 50%;
  border: 2px solid ${({ $active }) => $active ? styles.colors.primary600 : '#cbd5e1'};
  box-shadow: ${({ $active }) => $active ? `inset 0 0 0 3px #fff` : 'none'};
  background: ${({ $active }) => $active ? styles.colors.primary600 : '#fff'};
`;

const OptionText = styled.span`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

const OptionTitle = styled.span`
  font-family: NunitoSans;
  font-size: 14px;
  font-weight: 700;
  color: ${styles.colors.black600};
`;

const OptionSub = styled.span`
  font-family: NunitoSans;
  font-size: 12px;
  line-height: 18px;
  color: ${styles.colors.black300};
`;

const Warn = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-family: NunitoSans;
  font-size: 12px;
  font-weight: 600;
  color: #b45309;
`;

const Footer = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
  padding-top: 4px;
`;

const GhostBtn = styled.button`
  border: none;
  background: none;
  cursor: pointer;
  padding: 9px 8px;
  font-family: NunitoSans;
  font-size: 13px;
  font-weight: 700;
  line-height: 20px;
  color: #64748B;
  &:hover { color: #334155; }
  &:disabled { opacity: 0.5; cursor: default; }
`;

const PrimaryBtn = styled.button`
  border: none;
  border-radius: 8px;
  padding: 11px 20px;
  background: ${styles.colors.primary600};
  color: #fff;
  cursor: pointer;
  font-family: NunitoSans;
  font-size: 13px;
  font-weight: 700;
  line-height: 20px;
  &:hover { background: ${styles.colors.primary400}; }
  &:disabled { opacity: 0.6; cursor: default; }
`;
